/**
 * The geometry a "draw inside a boundary" control needs, as pure functions.
 *
 * Paired with `map-draw.controller.ts`, which owns the Leaflet side. Everything
 * that can be decided without a map lives here so it can be tested as a table
 * of fixtures: what a typed value means, what a drawn shape reads as, whether a
 * shape is inside the boundary, and what string to hand back.
 *
 * WHY THIS IS NOT THE AIS OBSERVATION MATCHER. `ais/shared/models/
 * ais-observations.model.ts` parses the same coordinate grammar, and the
 * duplication is deliberate: that file answers "which drawn vessels satisfy a
 * stored observation" and must be as TOLERANT as the tip engine, while this one
 * answers "is what the user just typed or drew a usable value" and must be
 * STRICTER than either — it is the place a mistake can still be corrected.
 * Merging them would force one set of rules on two opposite jobs.
 *
 * Pure: no `@angular/*`, no `leaflet`, no `this` (rule R1).
 */

// eslint-disable-next-line @typescript-eslint/no-var-requires
const geoParse = require('wellknown');

/** `[lng, lat]` — the order everything in this app stores and the API expects. */
export type LngLat = [number, number];

/** What the user marked: one place, or a closed ring. */
export interface DrawnShape {
  kind: 'point' | 'area';
  /** Set when `kind === 'point'`. */
  point?: LngLat;
  /** Set when `kind === 'area'` — closed, so first and last are equal. */
  ring?: LngLat[];
}

/** What the read-back line says about a shape. */
export interface ShapeSummary {
  /** "A place" / "An area". */
  title: string;
  /** "25.161° N, 55.342° E" / "4 corners: 132 sq km". */
  detail: string;
}

/** Parse result: exactly one of `shape` and `error` is set. */
export interface ParsedValue {
  shape: DrawnShape | null;
  error: string | null;
}

/**
 * Metres inside which a vessel counts as "at" a marked place — the tip engine's
 * own tolerance (`config.voyageRadarLocationToleranceMeters`, defaulted the
 * same way in `operators-service/ais/predicates/location.js`). Stated here so
 * the disc the map draws is the rule, not a decoration near it.
 */
export const POINT_TOLERANCE_M = 500;

const isNum = (n: any): n is number => typeof n === 'number' && Number.isFinite(n);

function inRange(lng: number, lat: number): boolean {
  return isNum(lng) && isNum(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90;
}

/** True when the ring already repeats its first vertex at the end. */
export function isRingClosed(ring: LngLat[]): boolean {
  if (ring.length < 2) return false;
  const first = ring[0];
  const last = ring[ring.length - 1];
  return first[0] === last[0] && first[1] === last[1];
}

/** Closes a ring by repeating its first vertex. Already-closed rings pass through. */
export function closeRing(ring: LngLat[]): LngLat[] {
  return isRingClosed(ring) ? ring : [...ring, ring[0]];
}

/** Corners a reader counts — the closing repeat is not one of them. */
export function cornerCount(ring: LngLat[]): number {
  return isRingClosed(ring) ? ring.length - 1 : ring.length;
}

/** Ray casting. The ring may be open or closed; both give the same answer. */
export function pointInRing(lng: number, lat: number, ring: LngLat[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const straddles = yi > lat !== yj > lat;
    if (straddles && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** Great-circle metres between two points. */
export function metresBetween(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Geodesic area in km², by the spherical shoelace. Good to a fraction of a
 * percent at the sizes a tasked AOI takes, and dependency-free — the figure is
 * shown to the user, never billed on.
 */
export function ringAreaKm2(ring: LngLat[]): number {
  const closed = closeRing(ring);
  if (closed.length < 4) return 0;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  let total = 0;
  for (let i = 0; i < closed.length - 1; i++) {
    const [lng1, lat1] = closed[i];
    const [lng2, lat2] = closed[i + 1];
    total += (toRad(lng2) - toRad(lng1)) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((total * R * R) / 2) / 1e6;
}

/** "25.161° N, 55.342° E" — hemispheres, because a bare minus is easy to miss. */
export function formatLngLat([lng, lat]: LngLat): string {
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(3)}° ${ns}, ${Math.abs(lng).toFixed(3)}° ${ew}`;
}

/** The value string the task stores: `lng,lat` or `lng,lat;…` with the ring closed. */
export function formatShapeValue(shape: DrawnShape | null): string {
  if (!shape) return '';
  const pair = ([lng, lat]: LngLat) => `${round6(lng)},${round6(lat)}`;
  if (shape.kind === 'point' && shape.point) return pair(shape.point);
  if (shape.kind === 'area' && shape.ring) return closeRing(shape.ring).map(pair).join(';');
  return '';
}

/** Six decimals is ~11 cm — past the point any AIS fix can tell apart. */
function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/** What the read-back line says. */
export function describeShape(shape: DrawnShape): ShapeSummary {
  if (shape.kind === 'point' && shape.point) {
    return { title: 'A place', detail: formatLngLat(shape.point) };
  }
  const ring = shape.ring ?? [];
  const corners = cornerCount(ring);
  const km2 = ringAreaKm2(ring);
  const area = km2 >= 10 ? Math.round(km2).toLocaleString() : km2.toFixed(1);
  return { title: 'An area', detail: `${corners} corners: ${area} sq km` };
}

/**
 * A typed value → a shape.
 *
 * Three grammars are accepted, in this order: WKT (so a `POINT (…)` or
 * `POLYGON ((…))` pasted out of a report or another screen works rather than
 * being shredded), then the stored `lng,lat;…` form, then a single `lng,lat`
 * pair. Every rejection names the actual fault — an out-of-range number says
 * so, rather than being answered with a lecture about coordinate order.
 */
export function parseCoordinateValue(text: string): ParsedValue {
  const raw = String(text ?? '').trim().replace(/;\s*$/, '');
  if (!raw) return { shape: null, error: null };

  const fromWkt = parseWkt(raw);
  if (fromWkt) return fromWkt;

  const pairs: LngLat[] = [];
  for (const part of raw.split(';')) {
    const chunk = part.trim();
    if (!chunk) continue;
    const bits = chunk.split(',');
    if (bits.length !== 2) {
      return {
        shape: null,
        error: `"${chunk}" is not a coordinate pair — each one is a longitude and a latitude separated by a comma.`,
      };
    }
    const lng = Number(bits[0].trim());
    const lat = Number(bits[1].trim());
    if (!isNum(lng) || !isNum(lat)) {
      return { shape: null, error: `"${chunk}" is not a pair of numbers.` };
    }
    if (Math.abs(lng) > 180) {
      return {
        shape: null,
        error: `Longitude must be between −180 and 180 — ${bits[0].trim()} is out of range. The first number is the longitude.`,
      };
    }
    if (Math.abs(lat) > 90) {
      return {
        shape: null,
        error: `Latitude must be between −90 and 90 — ${bits[1].trim()} is out of range. The second number is the latitude.`,
      };
    }
    pairs.push([lng, lat]);
  }

  return shapeFromPairs(pairs);
}

function shapeFromPairs(pairs: LngLat[]): ParsedValue {
  if (pairs.length === 0) return { shape: null, error: null };
  if (pairs.length === 1) return { shape: { kind: 'point', point: pairs[0] }, error: null };
  if (pairs.length === 2 || (pairs.length === 3 && isRingClosed(pairs))) {
    // Two points describe neither a place nor an area, and a "ring" of two
    // distinct corners is the same thing with the repeat already added.
    return {
      shape: null,
      error: 'An area needs at least 3 corners. One pair on its own marks a single place.',
    };
  }
  return { shape: { kind: 'area', ring: closeRing(pairs) }, error: null };
}

/** WKT in, shape out — or null when the text is not WKT at all. */
function parseWkt(raw: string): ParsedValue | null {
  if (!/^\s*(POINT|POLYGON)\s*\(/i.test(raw)) return null;
  let geometry: any = null;
  try {
    geometry = geoParse(raw);
  } catch {
    geometry = null;
  }
  if (!geometry?.type) {
    return { shape: null, error: 'That looks like WKT, but it could not be read.' };
  }
  if (geometry.type === 'Point') {
    const [lng, lat] = geometry.coordinates ?? [];
    if (!inRange(lng, lat)) {
      return { shape: null, error: 'That WKT point is outside the range of valid coordinates.' };
    }
    return { shape: { kind: 'point', point: [lng, lat] }, error: null };
  }
  const ring: LngLat[] = (geometry.coordinates?.[0] ?? []).filter((p: any) =>
    Array.isArray(p) && inRange(p[0], p[1])
  );
  if (ring.length < 4) {
    return { shape: null, error: 'That WKT polygon does not have enough usable corners.' };
  }
  return { shape: { kind: 'area', ring: closeRing(ring) }, error: null };
}

/**
 * Is `shape` inside `boundary`?
 *
 * The boundary is the tasked AOI, and the rule mirrors the host's own
 * `checkObservationInsideMap`: an AREA target contains a point by ray casting
 * and contains a ring only if EVERY corner is inside — a shape half out is half
 * a trigger the sweep can never see. A POINT target (a POI task) has no area to
 * be inside, so only that same point qualifies.
 *
 * A null boundary means "not known here", and everything passes: the host still
 * runs its own check, and refusing on missing data would block a task the
 * platform would have accepted.
 */
export function isShapeInside(shape: DrawnShape | null, boundary: DrawnShape | null): boolean {
  if (!shape) return false;
  if (!boundary) return true;

  if (boundary.kind === 'point') {
    if (shape.kind !== 'point' || !shape.point || !boundary.point) return false;
    // A metre of slack: the value is rounded to six decimals on the way out, so
    // an exact string compare would fail against the target it was copied from.
    return metresBetween(shape.point, boundary.point) <= 1;
  }

  const ring = boundary.ring ?? [];
  if (ring.length < 4) return true;
  if (shape.kind === 'point' && shape.point) {
    return pointInRing(shape.point[0], shape.point[1], ring);
  }
  const corners = shape.ring ?? [];
  return corners.length > 0 && corners.every(([lng, lat]) => pointInRing(lng, lat, ring));
}

/**
 * The tasked AOI as a shape, from whatever the host had to hand — a WKT string
 * (`target.geoJSON`), a GeoJSON geometry, or a Feature. Null when none of those
 * yield a usable boundary, which the caller reads as "do not constrain".
 */
export function boundaryFromGeometry(input: any): DrawnShape | null {
  if (!input) return null;

  if (typeof input === 'string') {
    const parsed = parseWkt(input.trim());
    return parsed?.shape ?? null;
  }

  const geometry = input?.type === 'Feature' ? input.geometry : input;
  if (geometry?.type === 'Point') {
    const [lng, lat] = geometry.coordinates ?? [];
    return inRange(lng, lat) ? { kind: 'point', point: [lng, lat] } : null;
  }
  if (geometry?.type === 'Polygon') {
    const ring: LngLat[] = (geometry.coordinates?.[0] ?? []).filter(
      (p: any) => Array.isArray(p) && inRange(p[0], p[1])
    );
    return ring.length >= 4 ? { kind: 'area', ring: closeRing(ring) } : null;
  }
  if (geometry?.type === 'MultiPolygon') {
    // The largest part. A multi-part AOI is rare and the alternative — refusing
    // to constrain at all — is worse than constraining to its main body.
    const rings: LngLat[][] = (geometry.coordinates ?? [])
      .map((poly: any) => (poly?.[0] ?? []).filter((p: any) => Array.isArray(p) && inRange(p[0], p[1])))
      .filter((r: LngLat[]) => r.length >= 4);
    if (!rings.length) return null;
    const biggest = rings.reduce((a, b) => (ringAreaKm2(b) > ringAreaKm2(a) ? b : a));
    return { kind: 'area', ring: closeRing(biggest) };
  }
  return null;
}
